/* media-gallery の active media 表示同期を custom element 本体から分離する */
(() => {
  const existingNamespace = window.AuremeMediaGallery || {};

  if (existingNamespace.ActiveMediaSync) {
    return;
  }

  const ScrollUtils = window.AuremeProductGallery?.ScrollUtils;
  const hasScrollUtilsApi =
    typeof ScrollUtils?.isNearScrollLeft === 'function' &&
    typeof ScrollUtils?.isReducedMotion === 'function' &&
    typeof ScrollUtils?.scrollLeftWithoutAnimation === 'function';
  if (!hasScrollUtilsApi) {
    console.error('[aureme-media-gallery-active-media-sync] scroll utils are not loaded.');
    return;
  }

  const DEFAULT_ACTIVE_MEDIA_OPTIONS = {
    thumbnailAlign: 'visible',
    thumbnailAnchorRatio: 0.4,
    allowViewportScroll: true,
    behavior: 'smooth',
    viewerScrollBehavior: 'auto',
    skipImmediateGallerySync: false,
    lockCounterUntilViewerScrollSettled: false,
    announce: true,
    playMedia: true,
    scrollThumbnail: true,
    scrollViewer: true,
  };
  const ACTIVE_MEDIA_CHANGE_EVENT = 'aureme:pdp:active-media-change';

  /* MediaGallery の状態と DOM 同期メソッドを使い、表示中メディアの副作用だけを扱う */
  class ActiveMediaSync {
    constructor(gallery) {
      this.gallery = gallery;
      this.stickyHeader = null;
    }

    get elements() {
      return this.gallery.elements;
    }

    get navigationItems() {
      return this.gallery.navigationItems;
    }

    getActiveMediaOptions(options = {}) {
      return {
        ...DEFAULT_ACTIVE_MEDIA_OPTIONS,
        ...options,
      };
    }

    getActiveMediaItem() {
      return this.elements.viewer?.querySelector('.product__media-item.is-active') || null;
    }

    getNavigationItem(mediaId) {
      return this.navigationItems.find((item) => item.dataset.target === mediaId);
    }

    /*
      PC のスライダー表示では、スライド移動イベントが来ても
      ユーザーが選択済みの表示メディア(activeMedia)を優先して維持する。
      スクロール由来の currentMedia に追従して選択サムネイルが意図せず変わるのを防ぐ。
    */
    shouldKeepDesktopActiveMedia(currentMediaId, activeMediaId) {
      if (!this.gallery.mqlDesktop.matches) return false;
      if (!this.elements.viewer?.matches?.('product-gallery-slider')) return false;
      if (!currentMediaId || !activeMediaId) return false;
      return currentMediaId !== activeMediaId;
    }

    updateActiveMediaState(mediaId) {
      const activeMedia =
        this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
        this.elements.viewer.querySelector('[data-media-id]');
      if (!activeMedia) return null;

      for (const element of this.elements.viewer.querySelectorAll('[data-media-id]')) {
        element.classList.remove('is-active');
      }
      activeMedia.classList.add('is-active');
      this.gallery.dispatchEvent(
        new CustomEvent(ACTIVE_MEDIA_CHANGE_EVENT, {
          bubbles: true,
          detail: { mediaId: activeMedia.dataset.mediaId },
        }),
      );

      return activeMedia;
    }

    syncFromSlideChanged(event) {
      if (this.gallery._isInitialSyncing) return;

      const currentElement = event?.detail?.currentElement;
      const currentMediaId = currentElement?.dataset?.mediaId;
      const activeMedia = this.getActiveMediaItem();
      const activeMediaId = activeMedia?.dataset?.mediaId;
      const shouldKeepCurrentActiveMedia = this.shouldKeepDesktopActiveMedia(
        currentMediaId,
        activeMediaId,
      );
      const nextMediaId = shouldKeepCurrentActiveMedia ? activeMediaId : currentMediaId;
      if (!nextMediaId) return;

      const navigationItem = this.getNavigationItem(nextMediaId);
      if (!navigationItem) return;
      const resolvedActiveMedia =
        shouldKeepCurrentActiveMedia && activeMedia
          ? activeMedia
          : this.updateActiveMediaState(nextMediaId) || currentElement;
      if (!resolvedActiveMedia) return;

      this.syncActiveImageState(resolvedActiveMedia);
      this.gallery.syncNavigationState(nextMediaId);
      this.gallery.updateCounter(navigationItem.dataset.mediaPosition);
      if (!this.gallery._allowSlideChangeAnnouncements) return;
      this.announceLiveRegion(resolvedActiveMedia, navigationItem.dataset.mediaPosition);
    }

    /* DOM順序は保持したまま、アクティブ状態とスクロール位置だけを更新する */
    setActiveMedia(mediaId, options = {}) {
      const activeMediaOptions = this.getActiveMediaOptions(options);
      const activeMedia = this.updateActiveMediaState(mediaId);
      if (!activeMedia) return;
      const activeNavigationItem = this.getNavigationItem(mediaId);
      if (!this.shouldLockViewerCounter(activeMedia, activeMediaOptions)) {
        this.elements.viewer?.cancelCounterLock?.();
      }

      this.syncActiveImageState(activeMedia);
      this.preventStickyHeader();
      /* サムネイル列の高さ同期とスクロール位置更新を単一フレームにまとめる */
      requestAnimationFrame(() => {
        this.gallery.syncThumbnailHeight();
        if (
          activeMediaOptions.scrollViewer &&
          (!this.gallery.mql.matches || this.elements.thumbnails)
        ) {
          this.syncViewerCounterLock(activeMedia, activeNavigationItem, activeMediaOptions);
          this.scrollActiveMediaIntoViewer(activeMedia, activeMediaOptions.viewerScrollBehavior);
        }
        if (!activeMediaOptions.allowViewportScroll) return;
        const activeMediaRect = activeMedia.getBoundingClientRect();
        /* 画像がすでにビューポート内にある場合はスクロールしない */
        if (activeMediaRect.top > -0.5) return;
        const top = activeMediaRect.top + window.scrollY;
        window.scrollTo({ top: top, behavior: activeMediaOptions.behavior });
      });
      if (activeMediaOptions.playMedia) {
        this.playActiveMedia(activeMedia);
      }

      this.gallery.syncNavigationState(mediaId, activeMediaOptions);
      if (activeNavigationItem) {
        this.gallery.updateCounter(activeNavigationItem.dataset.mediaPosition);
        if (activeMediaOptions.announce) {
          this.announceLiveRegion(activeMedia, activeNavigationItem.dataset.mediaPosition);
        }
      }
    }

    /*
      smooth scroll 中は scroll 由来の counter 更新が先に進むため、
      目標位置へ収束するまで選択済みページを viewer 側で固定する。
    */
    syncViewerCounterLock(activeMedia, activeNavigationItem, activeMediaOptions) {
      const currentPage = Number(activeNavigationItem?.dataset?.mediaPosition);
      if (
        !this.shouldLockViewerCounter(activeMedia, activeMediaOptions) ||
        !Number.isFinite(currentPage)
      ) {
        this.elements.viewer?.cancelCounterLock?.();
        return;
      }

      this.elements.viewer.lockCounterUntilScrollSettled({
        currentPage,
        targetLeft: activeMedia.offsetLeft,
      });
    }

    shouldLockViewerCounter(activeMedia, activeMediaOptions) {
      const shouldLock =
        activeMediaOptions.lockCounterUntilViewerScrollSettled ||
        activeMediaOptions.viewerScrollBehavior === 'smooth';
      if (!shouldLock) return false;
      if (this.shouldDisableSmoothViewerScroll(activeMediaOptions.viewerScrollBehavior))
        return false;
      if (typeof this.elements.viewer?.lockCounterUntilScrollSettled !== 'function') return false;

      const scrollContainer = activeMedia.parentElement;
      if (!scrollContainer?.scrollTo) return false;

      return !ScrollUtils.isNearScrollLeft(scrollContainer, activeMedia.offsetLeft);
    }

    scrollActiveMediaIntoViewer(activeMedia, viewerScrollBehavior) {
      const scrollContainer = activeMedia.parentElement;
      if (!scrollContainer?.scrollTo) return;

      const nextScrollLeft = activeMedia.offsetLeft;
      if (ScrollUtils.isNearScrollLeft(scrollContainer, nextScrollLeft)) return;

      if (this.shouldDisableSmoothViewerScroll(viewerScrollBehavior)) {
        this.scrollViewerWithoutAnimation(scrollContainer, nextScrollLeft);
        return;
      }

      scrollContainer.scrollTo({
        left: nextScrollLeft,
        behavior: viewerScrollBehavior,
      });
    }

    shouldDisableSmoothViewerScroll(viewerScrollBehavior) {
      return viewerScrollBehavior === 'smooth' && ScrollUtils.isReducedMotion();
    }

    scrollViewerWithoutAnimation(scrollContainer, nextScrollLeft) {
      ScrollUtils.scrollLeftWithoutAnimation(scrollContainer, nextScrollLeft);
    }

    getImageMediaItems() {
      return Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []).filter(
        (item) => item.querySelector('.product__media-image'),
      );
    }

    /* placeholder 画像は fetchpriority 対象外のため、loading と分けて同期する */
    syncImageLoadingPriority(image, shouldPrioritize, { syncFetchPriority = false } = {}) {
      if (!image.hasAttribute('data-aureme-default-loading')) {
        image.dataset.auremeDefaultLoading = image.getAttribute('loading') || '';
      }

      if (shouldPrioritize) {
        image.setAttribute('loading', 'eager');
      } else if (image.dataset.auremeDefaultLoading) {
        image.setAttribute('loading', image.dataset.auremeDefaultLoading);
      } else {
        image.removeAttribute('loading');
      }

      if (!syncFetchPriority) return;

      if (shouldPrioritize) {
        image.setAttribute('fetchpriority', 'high');
      } else {
        image.removeAttribute('fetchpriority');
      }
    }

    syncActiveImageState(activeItem) {
      const activeMediaId = activeItem?.dataset?.mediaId;
      if (!activeMediaId) return;

      for (const item of this.getImageMediaItems()) {
        const image = item.querySelector('.product__media-image');
        if (!image) continue;

        const isActive = item.dataset.mediaId === activeMediaId;
        this.syncImageLoadingPriority(image, isActive, { syncFetchPriority: true });
        this.syncBlurPlaceholder(item, image, isActive);
      }
    }

    syncBlurPlaceholder(item, image, isActive) {
      const media = item.querySelector('.product__media[data-aureme-blur-placeholder-src]');
      if (!media) return;

      const placeholder = this.ensureBlurPlaceholder(media, isActive);
      const placeholderImage = placeholder?.querySelector('.product__media-blur-placeholder-image');
      if (placeholderImage) {
        const shouldPrioritizePlaceholder = isActive && !this.gallery._isInitialSyncing;
        this.syncImageLoadingPriority(placeholderImage, shouldPrioritizePlaceholder);
      }
      if (!placeholder || !isActive) return;

      if (image.complete) {
        this.markBlurPlaceholderLoaded(item);
        return;
      }

      this.markBlurPlaceholderLoading(item);
      this.watchBlurPlaceholderImage(item, image);
    }

    ensureBlurPlaceholder(media, shouldCreate = false) {
      let placeholder = media.querySelector('.product__media-blur-placeholder');
      if (placeholder || !shouldCreate) return placeholder;

      const src = media.dataset.auremeBlurPlaceholderSrc;
      if (!src) return null;

      const image = document.createElement('img');
      image.src = src;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 176;
      image.height = 176;
      image.className = 'product__media-blur-placeholder-image';

      placeholder = document.createElement('span');
      placeholder.className = 'product__media-blur-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.append(image);
      media.prepend(placeholder);

      return placeholder;
    }

    markBlurPlaceholderLoading(item) {
      item.classList.add('product__media-item--image-loading');
      item.classList.remove('product__media-item--image-loaded');
    }

    markBlurPlaceholderLoaded(item) {
      item.classList.remove('product__media-item--image-loading');
      item.classList.add('product__media-item--image-loaded');
      this.removeBlurPlaceholderListeners(item);
    }

    /*
      active 画像の load/error だけを監視し、同じ item に残る handler を外して
      loading class の残留を防ぐ。
    */
    watchBlurPlaceholderImage(item, image) {
      this.removeBlurPlaceholderListeners(item);

      const handleReady = () => {
        this.markBlurPlaceholderLoaded(item);
      };

      item._auremeBlurPlaceholderImage = image;
      item._auremeBlurPlaceholderReadyHandler = handleReady;
      image.addEventListener('load', handleReady, { once: true });
      image.addEventListener('error', handleReady, { once: true });
    }

    removeBlurPlaceholderListeners(item) {
      const image = item._auremeBlurPlaceholderImage;
      const handler = item._auremeBlurPlaceholderReadyHandler;

      if (image && handler) {
        image.removeEventListener('load', handler);
        image.removeEventListener('error', handler);
      }

      item._auremeBlurPlaceholderImage = null;
      item._auremeBlurPlaceholderReadyHandler = null;
    }

    /*
      対象 image に残る load handler を外し、同じ画像で番号読み上げを重複させない。
    */
    removeLiveRegionAnnounceListener(image) {
      const handler = image?._auremeLiveRegionAnnounceHandler;
      if (image && handler) {
        image.removeEventListener('load', handler);
      }
      if (image) {
        image._auremeLiveRegionAnnounceHandler = null;
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__media-image');
      if (!image) return;
      const announce = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position,
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      if (image.complete) {
        this.removeLiveRegionAnnounceListener(image);
        announce();
      } else {
        this.removeLiveRegionAnnounceListener(image);
        const handleLoad = () => {
          image._auremeLiveRegionAnnounceHandler = null;
          announce();
        };
        image._auremeLiveRegionAnnounceHandler = handleLoad;
        image.addEventListener('load', handleLoad, { once: true });
      }
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    disconnect() {
      for (const item of this.getImageMediaItems()) {
        this.removeBlurPlaceholderListeners(item);
        this.removeLiveRegionAnnounceListener(item.querySelector('.product__media-image'));
      }
    }
  }

  Object.assign(existingNamespace, { ActiveMediaSync });
  window.AuremeMediaGallery = existingNamespace;
})();
